// Module 7 — Revenue & Sales Module (RS-01 to RS-06)
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtPKR, fmtDate, fmtDT } from '../../utils/helpers';
import { Spinner, EmptyState, Modal, Pagination } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Phone, Target, Edit2, Trash2 } from 'lucide-react';

// ── RS-01/02/03 Corporate Account Management ──────────────────────────────────
export function CorporatePage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoad]      = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState({ companyName:'', email:'', mobile:'', creditLimit:'100000', creditDays:'30', discountPct:'0', tariffId:'', taxId:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoad(true);
    api.get('/corporate').then(r=>r.data).then(r=>{ if(r.success) setAccounts(r.data||[]); }).finally(()=>setLoad(false));
  }, []);
  useEffect(()=>{ load(); },[load]);

  const create = async () => {
    if (!form.companyName||!form.email) { toast.error('Company name and email required'); return; }
    try {
      if (editId) {
        await api.patch('/corporate/'+editId, form).then(r=>r.data);
        toast.success('Account updated');
      } else {
        const r = await api.post('/corporate', form).then(r=>r.data);
        if (!r.success) throw new Error(r.message);
        toast.success('Corporate account created');
      }
      setShowAdd(false); setEditId(null); load();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  return (
    <AppShell title="Corporate Accounts" actions={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> New Account</button>}>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : accounts.length ? (
            <table>
              <thead><tr><th>Company</th><th>Email</th><th>Credit Limit</th><th>Used</th><th>Available</th><th>Days</th><th>Discount</th><th>KYC</th><th>Actions</th></tr></thead>
              <tbody>
                {accounts.map(a=>(
                  <tr key={a.customer_id}>
                    <td><div style={{ fontWeight:600 }}>{a.company_name}</div><div className="text-sm text-muted">{a.name}</div></td>
                    <td className="text-sm">{a.email}</td>
                    <td style={{ fontWeight:500 }}>{fmtPKR(a.credit_limit)}</td>
                    <td style={{ color:'var(--warning)' }}>{fmtPKR(a.credit_used)}</td>
                    <td style={{ color:'var(--success)', fontWeight:600 }}>{fmtPKR(a.credit_limit-a.credit_used)}</td>
                    <td>{a.credit_days}d</td>
                    <td>{a.discount_pct||0}%</td>
                    <td><span className={`badge ${a.kyc_status==='VERIFIED'?'badge-success':a.kyc_status==='REJECTED'?'badge-danger':'badge-warning'}`} style={{ fontSize:'.65rem' }}>{a.kyc_status}</span></td>
                    <td><div style={{ display:'flex', gap:4 }}>
                      <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }}
                        onClick={()=>{ setForm({ companyName:a.company_name||'', email:a.email||'', mobile:a.mobile||'', creditLimit:a.credit_limit||'', creditDays:a.credit_days||'30', discountPct:a.discount_pct||'0', tariffId:'', taxId:a.tax_id||'' }); setEditId(a.customer_id); setShowAdd(true); }}>
                        <Edit2 size={11}/>
                      </button>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                        onClick={async()=>{ if(!window.confirm('Deactivate account for '+a.company_name+'?')) return; try{ await api.patch('/corporate/'+a.customer_id, { isActive:false }); toast.success('Account deactivated'); load(); }catch(e){ toast.error(e.response?.data?.message||'Failed'); } }}>
                        <Trash2 size={11}/>
                      </button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="🏢" title="No corporate accounts" message={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}>Add First Account</button>}/>}
        </div>
      </div>
      <Modal open={showAdd} onClose={()=>{setShowAdd(false);setEditId(null);}} title={editId?"Edit Account":"New Corporate Account"}>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Company Name <span className="req">*</span></label><input className="form-control" value={form.companyName} onChange={set('companyName')}/></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Email <span className="req">*</span></label><input className="form-control" type="email" value={form.email} onChange={set('email')}/></div>
            <div className="form-group"><label className="form-label">Mobile</label><input className="form-control" value={form.mobile} onChange={set('mobile')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Credit Limit (PKR)</label><input className="form-control" type="number" value={form.creditLimit} onChange={set('creditLimit')}/></div>
            <div className="form-group"><label className="form-label">Credit Days</label><input className="form-control" type="number" value={form.creditDays} onChange={set('creditDays')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Discount %</label><input className="form-control" type="number" min="0" max="50" value={form.discountPct} onChange={set('discountPct')}/></div>
            <div className="form-group"><label className="form-label">Tax ID (NTN)</label><input className="form-control" placeholder="NTN-1234567" value={form.taxId} onChange={set('taxId')}/></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={create}>{editId?"Save Changes":"Create Account"}</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── RS-04/05/06 Sales Pipeline & Lead Management ──────────────────────────────
export function LeadsPage() {
  const [leads, setLeads]       = useState([]);
  const [loading, setLoad]      = useState(true);
  const [page, setPage]         = useState(1);
  const [pagination, setPagination] = useState({});
  const [statusFilter, setStatus] = useState('');
  const [showAdd, setShowAdd]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [callForm, setCallForm] = useState({ duration:'', outcome:'SPOKE', notes:'', nextAction:'' });
  const [form, setForm]         = useState({ companyName:'', contactName:'', contactPhone:'', contactEmail:'', source:'COLD_CALL', expectedValue:'', notes:'' });
  const setF = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const setC = k => e => setCallForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoad(true);
    const p = new URLSearchParams({ page, limit:20, ...(statusFilter&&{ status:statusFilter }) });
    api.get('/leads?'+p).then(r=>r.data).then(r=>{ if(r.success){ setLeads(r.data||[]); setPagination(r.pagination||{}); } }).finally(()=>setLoad(false));
  }, [page, statusFilter]);
  useEffect(()=>{ load(); },[load]);

  const create = async () => {
    if (!form.companyName||!form.contactName) { toast.error('Company and contact name required'); return; }
    try {
      const r = await api.post('/leads', form).then(r=>r.data);
      if (r.success) { toast.success('Lead created'); setShowAdd(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const logCall = async (leadId) => {
    try {
      await api.post('/call-logs', { leadId, ...callForm, durationSeconds:parseInt(callForm.duration||0)*60 }).then(r=>r.data);
      toast.success('Call logged'); setSelected(null);
      load();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const updateStatus = async (leadId, status) => {
    try {
      await api.patch('/leads/'+leadId, { status }).then(r=>r.data);
      toast.success('Status updated'); load();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const STATUSES = ['NEW','CONTACTED','QUALIFIED','PROPOSAL_SENT','NEGOTIATING','WON','LOST'];
  const STATUS_COLOR = { NEW:'var(--gray-400)', CONTACTED:'var(--info)', QUALIFIED:'var(--navy)', PROPOSAL_SENT:'var(--amber)', NEGOTIATING:'var(--warning)', WON:'var(--success)', LOST:'var(--danger)' };

  const pipeline = STATUSES.reduce((m,s)=>({ ...m, [s]: leads.filter(l=>l.status===s).length }),{});

  return (
    <AppShell title="Lead Pipeline" actions={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> New Lead</button>}>
      {/* RS-04 Pipeline funnel */}
      <div style={{ display:'flex', gap:6, marginBottom:20, overflowX:'auto', padding:'4px 0' }}>
        {STATUSES.map(s=>(
          <div key={s} onClick={()=>setStatus(statusFilter===s?'':s)}
            style={{ flex:1, minWidth:90, padding:'10px 8px', borderRadius:8, textAlign:'center', cursor:'pointer',
              background:statusFilter===s?STATUS_COLOR[s]:STATUS_COLOR[s]+'18',
              border:'2px solid '+(statusFilter===s?STATUS_COLOR[s]:'transparent'), transition:'all .15s' }}>
            <div style={{ fontWeight:700, fontSize:'1.2rem', color:statusFilter===s?'white':STATUS_COLOR[s] }}>{pipeline[s]||0}</div>
            <div style={{ fontSize:'.62rem', color:statusFilter===s?'white':STATUS_COLOR[s], marginTop:2 }}>{s.replace(/_/g,' ')}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : leads.length ? (
            <table>
              <thead><tr><th>Company</th><th>Contact</th><th>Source</th><th>Value</th><th>Status</th><th>Next Follow-up</th><th>Actions</th></tr></thead>
              <tbody>
                {leads.map(l=>(
                  <tr key={l.lead_id}>
                    <td><div style={{ fontWeight:600 }}>{l.company_name}</div></td>
                    <td>
                      <div style={{ fontSize:'.82rem' }}>{l.contact_name}</div>
                      <div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{l.contact_phone}</div>
                    </td>
                    <td className="text-sm">{l.source?.replace(/_/g,' ')}</td>
                    <td style={{ fontWeight:500 }}>{l.expected_value>0?fmtPKR(l.expected_value):'—'}</td>
                    <td>
                      <select style={{ border:'none', background:'transparent', fontSize:'.75rem', fontWeight:600, color:STATUS_COLOR[l.status] }}
                        value={l.status} onChange={e=>updateStatus(l.lead_id, e.target.value)}>
                        {STATUSES.map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                      </select>
                    </td>
                    <td className="text-sm" style={{ color:l.next_follow_up&&new Date(l.next_follow_up)<new Date()?'var(--danger)':undefined }}>
                      {l.next_follow_up?fmtDate(l.next_follow_up):'—'}
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>setSelected(l)}>
                          <Phone size={11}/> Log Call
                        </button>
                        <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                          onClick={async()=>{ if(!window.confirm('Delete lead for '+l.company_name+'?')) return; try{ await api.delete('/leads/'+l.lead_id); toast.success('Lead deleted'); load(); }catch(e){ toast.error(e.response?.data?.message||'Failed'); } }}>
                          <Trash2 size={11}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📋" title="No leads yet" message={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}>Add First Lead</button>}/>}
        </div>
        <Pagination page={page} pages={pagination.pages||1} onPage={setPage}/>
      </div>

      {/* Add lead modal */}
      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="New Lead">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Company Name <span className="req">*</span></label><input className="form-control" value={form.companyName} onChange={setF('companyName')}/></div>
            <div className="form-group"><label className="form-label">Contact Name <span className="req">*</span></label><input className="form-control" value={form.contactName} onChange={setF('contactName')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={form.contactPhone} onChange={setF('contactPhone')}/></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={form.contactEmail} onChange={setF('contactEmail')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Source</label>
              <select className="form-control" value={form.source} onChange={setF('source')}>
                {['COLD_CALL','REFERRAL','WALK_IN','ONLINE','EXHIBITION','OTHER'].map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Expected Value (PKR/month)</label><input className="form-control" type="number" value={form.expectedValue} onChange={setF('expectedValue')}/></div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={3} value={form.notes} onChange={setF('notes')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={create}>Create Lead</button>
        </div>
      </Modal>

      {/* Log call modal */}
      <Modal open={!!selected} onClose={()=>setSelected(null)} title={'Log Call — '+(selected?.company_name||'')}>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Duration (minutes)</label><input className="form-control" type="number" value={callForm.duration} onChange={setC('duration')}/></div>
            <div className="form-group"><label className="form-label">Outcome</label>
              <select className="form-control" value={callForm.outcome} onChange={setC('outcome')}>
                {['SPOKE','NO_ANSWER','VOICEMAIL','CALLBACK_REQUESTED','NOT_INTERESTED'].map(o=><option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Call Notes</label><textarea className="form-control" rows={3} value={callForm.notes} onChange={setC('notes')}/></div>
          <div className="form-group"><label className="form-label">Next Action</label><input className="form-control" placeholder="e.g. Send rate sheet, Schedule demo" value={callForm.nextAction} onChange={setC('nextAction')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setSelected(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={()=>logCall(selected?.lead_id)}><Phone size={13}/> Save Call Log</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── RS-04 Sales Targets & Achievement ─────────────────────────────────────────
export function SalesTargetsPage() {
  const [data, setData]   = useState({ performance:[], targets:[] });
  const [loading, setLoad] = useState(true);
  const [showSet, setShowSet] = useState(false);
  const [form, setForm]   = useState({ empId:'', targetType:'REVENUE', targetValue:'', month:'', year: new Date().getFullYear() });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  useEffect(()=>{
    Promise.all([
      api.get('/sales/performance').then(r=>r.data).catch(()=>({ success:false })),
    ]).then(([perf])=>{
      setData({ performance: perf.success?perf.data||[]:[] });
    }).finally(()=>setLoad(false));
  },[]);

  const saveTarget = async () => {
    try {
      await api.post('/sales/targets', form).then(r=>r.data);
      toast.success('Target set'); setShowSet(false);
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  return (
    <AppShell title="Sales Targets & Achievement" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowSet(true)}><Target size={14}/> Set Target</button>
    }>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : data.performance.length ? (
            <table>
              <thead><tr><th>Sales Rep</th><th>Visits</th><th>Positive</th><th>Conversion</th><th>Revenue</th><th>Achievement</th></tr></thead>
              <tbody>
                {data.performance.map((p,i)=>(
                  <tr key={i}>
                    <td style={{ fontWeight:600 }}>{p.sales_rep_name||p.emp_name||'—'}</td>
                    <td>{p.total_visits||0}</td>
                    <td style={{ color:'var(--success)' }}>{p.positive_visits||0}</td>
                    <td>{p.total_visits>0?((p.positive_visits/p.total_visits)*100).toFixed(0)+'%':'—'}</td>
                    <td style={{ fontWeight:600 }}>{fmtPKR(p.revenue||0)}</td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ flex:1, height:6, background:'var(--gray-100)', borderRadius:3 }}>
                          <div style={{ height:'100%', borderRadius:3, background:'var(--success)', width:Math.min(100, p.achievement_pct||0)+'%' }}/>
                        </div>
                        <span style={{ fontSize:'.78rem', fontWeight:600 }}>{p.achievement_pct||0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="🎯" title="No performance data yet" />}
        </div>
      </div>
      <Modal open={showSet} onClose={()=>setShowSet(false)} title="Set Sales Target">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Target Type</label>
              <select className="form-control" value={form.targetType} onChange={set('targetType')}>
                {['REVENUE','VISITS','NEW_ACCOUNTS','SHIPMENTS'].map(t=><option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Target Value</label><input className="form-control" type="number" value={form.targetValue} onChange={set('targetValue')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Month</label>
              <select className="form-control" value={form.month} onChange={set('month')}>
                <option value="">All year</option>
                {Array.from({length:12},(_,i)=><option key={i+1} value={i+1}>{new Date(2000,i).toLocaleString('en',{month:'long'})}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Year</label><input className="form-control" type="number" value={form.year} onChange={set('year')}/></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowSet(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveTarget}>Save Target</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── RS-06 Sales Visits ────────────────────────────────────────────────────────
export function SalesPage() {
  const [visits, setVisits]   = useState([]);
  const [loading, setLoad]    = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editVisit, setEditVisit] = useState(null);
  const [form, setForm]       = useState({ customerId:'', visitType:'IN_PERSON', outcome:'POSITIVE', notes:'', checkinAt:'', checkoutAt:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoad(true);
    api.get('/sales/visits?limit=50').then(r=>r.data).then(r=>{ if(r.success) setVisits(r.data||[]); }).finally(()=>setLoad(false));
  },[]);
  useEffect(()=>{ load(); },[load]);

  const create = async () => {
    try {
      if (editVisit) {
        await api.patch('/sales/visits/'+editVisit.visit_id, form);
        toast.success('Visit updated');
      } else {
        const r = await api.post('/sales/visits', form).then(r=>r.data);
        if (r.success) toast.success('Visit recorded');
      }
      setShowAdd(false); setEditVisit(null); load();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const OUTCOME_COLOR = { POSITIVE:'var(--success)', NEGATIVE:'var(--danger)', NEUTRAL:'var(--gray-400)', FOLLOW_UP:'var(--amber)' };

  return (
    <AppShell title="Sales Visits" actions={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> Log Visit</button>}>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : visits.length ? (
            <table>
              <thead><tr><th>Customer</th><th>Type</th><th>Outcome</th><th>Check-in</th><th>Check-out</th><th>Sales Rep</th><th>Notes</th><th></th></tr></thead>
              <tbody>
                {visits.map(v=>(
                  <tr key={v.visit_id}>
                    <td style={{ fontWeight:500 }}>{v.company_name||v.customer_name||'—'}</td>
                    <td><span className="badge badge-info" style={{ fontSize:'.65rem' }}>{v.visit_type?.replace(/_/g,' ')}</span></td>
                    <td><span style={{ fontSize:'.72rem', fontWeight:600, color:OUTCOME_COLOR[v.outcome]||'var(--gray-400)' }}>{v.outcome}</span></td>
                    <td className="text-sm">{v.checkin_at?fmtDT(v.checkin_at):'—'}</td>
                    <td className="text-sm">{v.checkout_at?fmtDT(v.checkout_at):'—'}</td>
                    <td className="text-sm">{v.sales_rep_name||'—'}</td>
                    <td style={{ maxWidth:200, fontSize:'.75rem', color:'var(--gray-500)' }}>{v.notes?.slice(0,80)||'—'}</td>
                    <td><div style={{ display:'flex', gap:4 }}>
                      <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }}
                        onClick={()=>{ setEditVisit(v); setForm({ customerId:v.customer_id||'', visitType:v.visit_type||'IN_PERSON', outcome:v.outcome||'POSITIVE', notes:v.notes||'', checkinAt:v.checkin_at?v.checkin_at.slice(0,16):'', checkoutAt:v.checkout_at?v.checkout_at.slice(0,16):'' }); setShowAdd(true); }}>
                        <Edit2 size={11}/>
                      </button>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                        onClick={async()=>{ if(!window.confirm('Delete this visit record?')) return; try{ await api.delete('/sales/visits/'+v.visit_id); toast.success('Deleted'); load(); }catch(e){ toast.error(e.response?.data?.message||'Failed'); } }}>
                        <Trash2 size={11}/>
                      </button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="🤝" title="No visits recorded" />}
        </div>
      </div>
      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title={editVisit ? 'Edit Visit' : 'Log Sales Visit'}>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Visit Type</label>
              <select className="form-control" value={form.visitType} onChange={set('visitType')}>
                {['IN_PERSON','PHONE','VIDEO_CALL','EMAIL'].map(t=><option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Outcome</label>
              <select className="form-control" value={form.outcome} onChange={set('outcome')}>
                {['POSITIVE','NEGATIVE','NEUTRAL','FOLLOW_UP'].map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Check-in Time</label><input className="form-control" type="datetime-local" value={form.checkinAt} onChange={set('checkinAt')}/></div>
            <div className="form-group"><label className="form-label">Check-out Time</label><input className="form-control" type="datetime-local" value={form.checkoutAt} onChange={set('checkoutAt')}/></div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={4} value={form.notes} onChange={set('notes')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={create}>{editVisit ? 'Save Changes' : 'Save Visit'}</button>
        </div>
      </Modal>
    </AppShell>
  );
}
